import React, { useState } from 'react';

const CaseloadDiagnosisForm = ({ onSave, onCancel, existingData = [] }) => {
  // Initialize with existing data or one empty row
  const getInitialData = () => {
    if (existingData && existingData.length > 0) {
      return existingData.map(row => ({
        clientId: row.clientId || '',
        diagnosis: row.diagnosis || ''
      }));
    }
    return [{ clientId: '', diagnosis: '' }];
  };

  const [formData, setFormData] = useState(getInitialData());
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState(null);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const rowRefs = React.useRef({});
  const pendingScrollRef = React.useRef(false);

  // Common diagnoses for autocomplete suggestions
  const commonDiagnoses = [
    'Major Depressive Disorder',
    'Generalized Anxiety Disorder',
    'Post-Traumatic Stress Disorder',
    'Bipolar Disorder',
    'Bipolar I Disorder',
    'Bipolar II Disorder',
    'Attention-Deficit/Hyperactivity Disorder',
    'Social Anxiety Disorder',
    'Panic Disorder',
    'Obsessive-Compulsive Disorder',
    'Persistent Depressive Disorder',
    'Adjustment Disorder',
    'Borderline Personality Disorder',
    'Schizophrenia',
    'Schizoaffective Disorder',
    'Alcohol Use Disorder',
    'Substance Use Disorder',
    'Autism Spectrum Disorder'
  ];

  // Add a single row
  const addRow = () => {
    pendingScrollRef.current = true;
    setFormData([...formData, { clientId: '', diagnosis: '' }]);
  };

  // Add multiple rows
  const addMultipleRows = (count) => {
    pendingScrollRef.current = true;
    const newRows = Array(count).fill().map(() => ({
      clientId: '',
      diagnosis: ''
    }));
    setFormData([...formData, ...newRows]);
  };

  // Auto-scroll to the newly added row(s) after they render
  React.useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const el = rowRefs.current[formData.length - 1];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formData.length]);

  // Remove a row
  const removeRow = (index) => {
    if (formData.length > 1) {
      const newData = formData.filter((_, i) => i !== index);
      setFormData(newData);

      // Clear validation errors for removed row and reindex
      const newErrors = {};
      Object.keys(validationErrors).forEach(key => {
        const [rowIndex, field] = key.split('.');
        const oldIndex = parseInt(rowIndex);
        if (oldIndex < index) {
          newErrors[key] = validationErrors[key];
        } else if (oldIndex > index) {
          newErrors[`${oldIndex - 1}.${field}`] = validationErrors[key];
        }
      });
      setValidationErrors(newErrors);
    }
  };

  // Search for a client by ID and scroll to their row
  const handleSearch = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return;

    const matchIndex = formData.findIndex(row =>
      row.clientId.trim().toLowerCase() === query
    );

    if (matchIndex !== -1) {
      setSearchStatus('found');
      setHighlightedRow(matchIndex);
      rowRefs.current[matchIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setSearchStatus('notfound');
      setTimeout(() => setSearchStatus(null), 2500);
    }
  };

  // Update a field value
  const updateField = (index, field, value) => {
    const newData = [...formData];
    newData[index] = { ...newData[index], [field]: value };
    setFormData(newData);

    // Clear validation error for this field
    const errorKey = `${index}.${field}`;
    if (validationErrors[errorKey]) {
      const newErrors = { ...validationErrors };
      delete newErrors[errorKey];
      setValidationErrors(newErrors);
    }
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    let hasErrors = false;

    formData.forEach((row, index) => {
      // Validate Client ID
      if (!row.clientId.trim()) {
        errors[`${index}.clientId`] = 'Client ID is required';
        hasErrors = true;
      }

      // Validate Diagnosis
      if (!row.diagnosis.trim()) {
        errors[`${index}.diagnosis`] = 'Diagnosis is required';
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Filter out empty rows and format data
      const validData = formData
        .filter(row => row.clientId.trim() && row.diagnosis.trim())
        .map(row => ({
          clientId: row.clientId.trim(),
          diagnosis: row.diagnosis.trim()
        }));

      if (validData.length === 0) {
        setValidationErrors({ form: 'At least one complete row is required' });
        setIsSubmitting(false);
        return;
      }

      await onSave(validData);
    } catch (error) {
      console.error('Error saving data:', error);
      setValidationErrors({ form: 'Failed to save data. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Count valid rows
  const validRowCount = formData.filter(
    row => row.clientId.trim() && row.diagnosis.trim()
  ).length;

  return (
    <div className="bg-white rounded-xl shadow-md">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-100 to-blue-200 rounded-t-xl p-6 border-b border-blue-300">
        <div className="flex items-center justify-center mb-2">
          <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center mr-4">
            <span className="text-white font-bold text-xl">🏥</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Caseload Diagnosis Data Entry</h2>
        </div>
        <p className="text-gray-700 text-center">
          Enter client diagnosis information below. Each client can have multiple diagnoses.
        </p>
      </div>

      {/* Form Content */}
      <div className="p-6">
        {/* Form-level error */}
        {validationErrors.form && (
          <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700">
            {validationErrors.form}
          </div>
        )}

        {/* Add Rows Buttons + Search */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={addRow}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            + Add Row
          </button>
          <button
            onClick={() => addMultipleRows(5)}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
          >
            + Add 5 Rows
          </button>
          <button
            onClick={() => addMultipleRows(10)}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
          >
            + Add 10 Rows
          </button>

          {/* Search Client ID */}
          <div className="flex items-center gap-1 ml-auto">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchStatus(null); setHighlightedRow(null); }}
                onFocus={() => setHighlightedRow(null)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search Client ID..."
                className={`pl-8 pr-3 py-2 text-sm border rounded-lg w-48 focus:outline-none focus:ring-2 transition-colors ${
                  searchStatus === 'notfound'
                    ? 'border-red-400 bg-red-50 focus:ring-red-300'
                    : searchStatus === 'found'
                    ? 'border-green-400 bg-green-50 focus:ring-green-300'
                    : 'border-gray-300 focus:ring-blue-300'
                }`}
              />
              {searchStatus === 'notfound' && (
                <span className="absolute -bottom-5 left-0 text-xs text-red-600 whitespace-nowrap">No match found</span>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go
            </button>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                  Client ID
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                  Diagnosis
                </th>
                <th className="border border-gray-300 px-4 py-3 text-center font-semibold text-gray-900 w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {formData.map((row, index) => (
                <tr
                  key={index}
                  ref={el => rowRefs.current[index] = el}
                  className={`transition-colors duration-300 ${
                    highlightedRow === index
                      ? 'bg-yellow-100 ring-2 ring-yellow-400'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Client ID */}
                  <td className="border border-gray-300 px-2 py-2">
                    <input
                      type="text"
                      value={row.clientId}
                      onChange={(e) => updateField(index, 'clientId', e.target.value)}
                      placeholder="Client-001"
                      className={`w-full px-3 py-2 border rounded-md text-sm ${
                        validationErrors[`${index}.clientId`]
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                    {validationErrors[`${index}.clientId`] && (
                      <div className="text-red-600 text-xs mt-1">
                        {validationErrors[`${index}.clientId`]}
                      </div>
                    )}
                  </td>

                  {/* Diagnosis */}
                  <td className="border border-gray-300 px-2 py-2">
                    <input
                      type="text"
                      value={row.diagnosis}
                      onChange={(e) => updateField(index, 'diagnosis', e.target.value)}
                      placeholder="Major Depressive Disorder"
                      list={`diagnosis-list-${index}`}
                      className={`w-full px-3 py-2 border rounded-md text-sm ${
                        validationErrors[`${index}.diagnosis`]
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                    <datalist id={`diagnosis-list-${index}`}>
                      {commonDiagnoses.map((dx, i) => (
                        <option key={i} value={dx} />
                      ))}
                    </datalist>
                    {validationErrors[`${index}.diagnosis`] && (
                      <div className="text-red-600 text-xs mt-1">
                        {validationErrors[`${index}.diagnosis`]}
                      </div>
                    )}
                  </td>

                  {/* Remove Button */}
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <button
                      onClick={() => removeRow(index)}
                      disabled={formData.length === 1}
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        formData.length === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
          <span className="text-gray-600">
            {validRowCount} of {formData.length} rows complete
          </span>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSubmitting || validRowCount === 0}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                isSubmitting || validRowCount === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? 'Saving...' : `Save Data (${validRowCount} records)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseloadDiagnosisForm;
